'use client';

import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';

type VenueMediaCarouselProps = {
  venueName: string;
  photos: string[];
  latitude?: number;
  longitude?: number;
  address?: string;
  className?: string;
  sizes?: string;
  mapFallbackText?: string;
};

export function VenueMediaCarousel({
  venueName,
  photos,
  latitude,
  longitude,
  address,
  className = 'h-80',
  sizes = '(max-width: 1024px) 100vw, 920px',
  mapFallbackText = 'Map preview is unavailable for this venue.',
}: VenueMediaCarouselProps) {
  const [index, setIndex] = useState(0);
  const mapEmbedUrl = useMemo(() => buildMapEmbedUrl({ latitude, longitude, address }), [latitude, longitude, address]);
  const slideCount = photos.length + 1;
  const isPhotoSlide = index < photos.length;

  useEffect(() => {
    if (index >= slideCount) {
      setIndex(0);
    }
  }, [index, slideCount]);

  const handleNext = useCallback(() => {
    if (slideCount <= 1) return;
    setIndex((current) => (current + 1) % slideCount);
  }, [slideCount]);

  const handlePrev = useCallback(() => {
    if (slideCount <= 1) return;
    setIndex((current) => (current - 1 + slideCount) % slideCount);
  }, [slideCount]);

  return (
    <div className={`relative overflow-hidden rounded-2xl border border-border/60 bg-muted ${className}`}>
      {isPhotoSlide ? (
        <Image
          key={photos[index]}
          src={photos[index]}
          alt={`${venueName} photo ${index + 1}`}
          fill
          sizes={sizes}
          className="object-cover object-center"
          unoptimized
        />
      ) : mapEmbedUrl ? (
        <iframe
          title={`${venueName} map`}
          src={mapEmbedUrl}
          className="size-full"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      ) : (
        <div className="flex size-full items-center justify-center px-5 text-center text-sm text-muted-foreground">
          {mapFallbackText}
        </div>
      )}

      {slideCount > 1 ? (
        <>
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/60"
            aria-label="Previous slide"
          >
            {'<'}
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur hover:bg-black/60"
            aria-label="Next slide"
          >
            {'>'}
          </button>
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1">
            {Array.from({ length: slideCount }).map((_, dotIndex) => (
              <span
                key={`dot-${dotIndex}`}
                className={`size-2 rounded-full ${dotIndex === index ? 'bg-white' : 'bg-white/50'}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function buildMapEmbedUrl({
  latitude,
  longitude,
  address,
}: {
  latitude?: number;
  longitude?: number;
  address?: string;
}): string | null {
  if (typeof latitude === 'number' && typeof longitude === 'number') {
    return `https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`;
  }

  const normalizedAddress = (address ?? '').trim();
  if (!normalizedAddress) return null;

  return `https://maps.google.com/maps?q=${encodeURIComponent(normalizedAddress)}&z=14&output=embed`;
}
